import { types, flow } from 'mobx-state-tree';
import axios from 'axios';
import { ApiStore } from './ApiStore';

// Document Defense Library: a company-wide list of documents every rep can
// practice on. The server never sends the question bank or expected answers,
// so nothing here can leak them; this store only ever holds the public
// projection returned by /companies/{id}/library/documents.
//
// Folders are one level of curator-managed grouping that every member sees;
// a document's folderId is null for Unfiled. Bulk adds run three uploads at a
// time and leave ingestion to the server (the page reloads the list while
// anything is queued or processing).

const LibraryDocument = types.model('LibraryDocument', {
    documentId: types.identifier,
    title: types.optional(types.string, ''),
    fileName: types.optional(types.string, ''),
    extension: types.optional(types.string, ''),
    contentType: types.optional(types.string, ''),
    size: types.optional(types.number, 0),
    status: types.optional(types.string, 'uploading'),
    error: types.maybeNull(types.string),
    documentType: types.maybeNull(types.string),
    documentSummary: types.maybeNull(types.string),
    pageCount: types.maybeNull(types.number),
    bankSize: types.optional(types.number, 0),
    sessionCount: types.optional(types.number, 0),
    lastTrainedAt: types.maybeNull(types.string),
    uploadedBy: types.optional(types.frozen(), {}),
    createdAt: types.maybeNull(types.string),
    updatedAt: types.maybeNull(types.string),
    folderId: types.maybeNull(types.string),
    mine: types.optional(types.frozen(), {}),
}).views(self => ({
    get isReady() { return self.status === 'ready'; },
    // queued = waiting for a worker (bulk adds queue behind the worker cap).
    get isBusy() { return self.status === 'queued' || self.status === 'processing' || self.status === 'uploading'; },
    get hasError() { return self.status === 'error'; },
    get uploaderName() { return (self.uploadedBy && self.uploadedBy.name) || ''; },
    get myLastSessionAt() { return (self.mine && self.mine.lastSessionAt) || null; },
    get mySessionCount() { return (self.mine && self.mine.sessionCount) || 0; },
}));

const LibraryFolder = types.model('LibraryFolder', {
    folderId: types.identifier,
    name: types.optional(types.string, ''),
    createdAt: types.maybeNull(types.string),
});

const str = (v) => (typeof v === 'string' ? v : null);
const num = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

// Defensive: a malformed row must never throw inside an MST create/apply
// (the admin console has been taken down by exactly that before).
export const toDocumentSnapshot = (d = {}) => ({
    documentId: String(d.documentId || ''),
    title: str(d.title) || '',
    fileName: str(d.fileName) || '',
    extension: str(d.extension) || '',
    contentType: str(d.contentType) || '',
    size: num(d.size) || 0,
    status: str(d.status) || 'uploading',
    error: str(d.error),
    documentType: str(d.documentType),
    documentSummary: str(d.documentSummary),
    pageCount: num(d.pageCount),
    bankSize: num(d.bankSize) || 0,
    sessionCount: num(d.sessionCount) || 0,
    lastTrainedAt: str(d.lastTrainedAt),
    uploadedBy: (d.uploadedBy && typeof d.uploadedBy === 'object') ? d.uploadedBy : {},
    createdAt: str(d.createdAt),
    updatedAt: str(d.updatedAt),
    folderId: str(d.folderId),
    mine: (d.mine && typeof d.mine === 'object') ? d.mine : {},
});

export const toFolderSnapshot = (f = {}) => ({
    folderId: String(f.folderId || ''),
    name: str(f.name) || '',
    createdAt: str(f.createdAt),
});

const base = (companyId) => `/companies/${companyId}/library/documents`;
const foldersBase = (companyId) => `/companies/${companyId}/library/folders`;

export const UPLOADS_IN_FLIGHT = 3;

const _store = types.model('DocumentLibraryStore', {
    documents: types.optional(types.array(LibraryDocument), []),
    folders: types.optional(types.array(LibraryFolder), []),
    loadedForCompanyId: types.maybeNull(types.string),
});

export const DocumentLibraryStore = types
    .compose('DocumentLibraryStore', _store, ApiStore)
    .volatile(() => ({
        uploading: false,
        uploadStep: '',
        // One entry per file of the current bulk add: { key, fileName, title, status, error, documentId }
        // status: waiting | uploading | reading | error. Replaced wholesale so observers see each change.
        uploads: [],
        // When the last bulk add started; the page counts documents added since then.
        batchStartedAt: null,
        pollIntervalMs: 3000,
        pollMaxAttempts: 100,
    }))
    .views(self => ({
        get readyDocuments() { return self.documents.filter(d => d.isReady); },
        get busyDocuments() { return self.documents.filter(d => d.isBusy); },
        byId(documentId) { return self.documents.find(d => d.documentId === documentId) || null; },
        folderById(folderId) { return self.folders.find(f => f.folderId === folderId) || null; },
        // 'all' | 'unfiled' | a folderId
        documentsIn(selection) {
            if (!selection || selection === 'all') return self.documents.slice();
            if (selection === 'unfiled') return self.documents.filter(d => !d.folderId);
            return self.documents.filter(d => d.folderId === selection);
        },
        get unfiledCount() { return self.documents.filter(d => !d.folderId).length; },
        folderCount(folderId) { return self.documents.filter(d => d.folderId === folderId).length; },
        get uploadsPending() { return self.uploads.filter(u => u.status === 'waiting' || u.status === 'uploading').length; },
    }))
    .actions(self => ({
        setDocuments(list) {
            self.documents.replace((Array.isArray(list) ? list : []).filter(d => d && d.documentId).map(toDocumentSnapshot));
        },
        setFolders(list) {
            self.folders.replace((Array.isArray(list) ? list : []).filter(f => f && f.folderId).map(toFolderSnapshot));
        },
        upsert(doc) {
            if (!doc || !doc.documentId) return;
            const snap = toDocumentSnapshot(doc);
            const idx = self.documents.findIndex(d => d.documentId === snap.documentId);
            if (idx >= 0) self.documents[idx] = snap;
            else self.documents.unshift(snap);
        },
        remove(documentId) {
            const idx = self.documents.findIndex(d => d.documentId === documentId);
            if (idx >= 0) self.documents.splice(idx, 1);
        },
        setUploadStep(step) { self.uploadStep = step; },
        setUploading(flag) { self.uploading = flag; },
        setUploads(list) { self.uploads = list; },
        patchUpload(key, patch) {
            self.uploads = self.uploads.map(u => (u.key === key ? { ...u, ...patch } : u));
        },
        clearUploads() { self.uploads = []; },
        setBatchStartedAt(iso) { self.batchStartedAt = iso; },
        applyFolderLocally(documentIds, folderId) {
            const ids = new Set(documentIds);
            self.documents.forEach((d) => { if (ids.has(d.documentId)) d.folderId = folderId || null; });
        },

        load: flow(function* load(companyId) {
            const data = yield self.getApi(base(companyId));
            self.setDocuments(data ? data.documents : []);
            self.setFolders(data ? data.folders : []);
            self.loadedForCompanyId = companyId;
            return self.documents;
        }),

        refresh: flow(function* refresh(companyId, documentId) {
            const doc = yield self.getApi(`${base(companyId)}/${documentId}`);
            self.upsert(doc);
            return doc;
        }),

        // Polls the document until the worker has settled it (ready or error).
        waitUntilSettled: flow(function* waitUntilSettled(companyId, documentId) {
            for (let attempt = 0; attempt < self.pollMaxAttempts; attempt++) {
                const doc = yield self.refresh(companyId, documentId);
                if (doc && (doc.status === 'ready' || doc.status === 'error')) return doc;
                yield new Promise(resolve => setTimeout(resolve, self.pollIntervalMs));
            }
            return null;
        }),

        // One file: metadata POST -> presigned PUT straight to S3 -> complete.
        // The PUT uses bare axios with baseURL '' so the global bearer
        // interceptor stays out (S3 rejects an Authorization header on a
        // presigned URL); same trick as the Training Hub upload. Returns the
        // document as the server reported it after complete.
        uploadOne: flow(function* uploadOne(companyId, file, title, folderId) {
            const cleanTitle = title && title.trim() ? title.trim() : undefined;
            const created = yield self.postApi(base(companyId), {
                fileName: file.name,
                contentType: file.type || 'application/octet-stream',
                size: file.size,
                title: cleanTitle,
                ...(folderId ? { folderId } : {}),
            });
            yield axios.put(created.uploadUrl, file, {
                headers: { 'Content-Type': created.contentType },
                baseURL: '',
            });
            const completed = yield self.postApi(`${base(companyId)}/${created.documentId}/complete`, {});
            const doc = {
                documentId: created.documentId,
                title: cleanTitle || file.name,
                fileName: file.name,
                contentType: created.contentType,
                size: file.size,
                folderId: folderId || null,
                status: (completed && completed.status) || 'queued',
                error: (completed && completed.error) || null,
                createdAt: new Date().toISOString(),
            };
            self.upsert(doc);
            return doc;
        }),

        // Single add, kept for the one-off callers: upload, then poll that one
        // document until Murphy has read it.
        addDocument: flow(function* addDocument(companyId, file, title) {
            self.setUploading(true);
            try {
                self.setUploadStep('Preparing upload');
                const doc = yield self.uploadOne(companyId, file, title, null);
                self.setUploadStep('Murphy is reading the document');
                if (doc.status === 'error') return self.byId(doc.documentId);
                return yield self.waitUntilSettled(companyId, doc.documentId);
            } finally {
                self.setUploading(false);
                self.setUploadStep('');
            }
        }),

        // Bulk add: `items` is [{ key, file, title }]. Three uploads in flight;
        // a file that fails is marked and the rest continue. No polling here:
        // every document is handed to the server's queue, and the page reloads
        // the list while any document is still busy.
        addDocuments: flow(function* addDocuments(companyId, items, { folderId = null } = {}) {
            const list = (items || []).map((it, i) => ({
                key: it.key || `${i}-${it.file?.name || 'file'}`, fileName: it.file?.name || '', title: it.title || '',
                status: 'waiting', error: null, documentId: null,
            }));
            self.setUploads(list);
            self.setBatchStartedAt(new Date().toISOString());
            self.setUploading(true);
            const queue = (items || []).map((it, i) => ({ ...it, key: list[i].key }));
            const worker = async () => {
                while (queue.length) {
                    const it = queue.shift();
                    self.patchUpload(it.key, { status: 'uploading' });
                    try {
                        const doc = await self.uploadOne(companyId, it.file, it.title, folderId);
                        self.patchUpload(it.key, doc.status === 'error'
                            ? { status: 'error', error: doc.error, documentId: doc.documentId }
                            : { status: 'reading', documentId: doc.documentId });
                    } catch (e) {
                        self.patchUpload(it.key, { status: 'error', error: e?.message || 'Could not add this file.' });
                    }
                }
            };
            try {
                yield Promise.all(Array.from({ length: Math.min(UPLOADS_IN_FLIGHT, queue.length) }, worker));
            } finally {
                self.setUploading(false);
            }
            return self.uploads;
        }),

        retryProcessing: flow(function* retryProcessing(companyId, documentId) {
            const res = yield self.postApi(`${base(companyId)}/${documentId}/complete`, {});
            if (res && res.status === 'error') {
                yield self.refresh(companyId, documentId);
                return self.byId(documentId);
            }
            return yield self.waitUntilSettled(companyId, documentId);
        }),

        rename: flow(function* rename(companyId, documentId, title) {
            const doc = yield self.patchApi(`${base(companyId)}/${documentId}`, { title });
            self.upsert(doc);
            return doc;
        }),

        removeDocument: flow(function* removeDocument(companyId, documentId) {
            yield self.deleteApi(`${base(companyId)}/${documentId}`);
            self.remove(documentId);
        }),

        // `download: true` asks for a link that saves the file whatever its type
        // (the page uses it when the browser cannot show PDFs).
        getDownloadUrl: flow(function* getDownloadUrl(companyId, documentId, { download = false } = {}) {
            const res = yield self.getApi(`${base(companyId)}/${documentId}/download-url${download ? '?download=1' : ''}`);
            return res ? res.url : null;
        }),

        // Returns the ready session payload (question text + participants only);
        // the caller hands it to meetingStore.loadReadyQASession.
        startSession: flow(function* startSession(companyId, documentId, questionCount = 5) {
            return yield self.getApi(`${base(companyId)}/${documentId}/start-session?questionCount=${questionCount}`);
        }),

        // --- folders (curator) -------------------------------------------------

        createFolder: flow(function* createFolder(companyId, name) {
            const folder = yield self.postApi(foldersBase(companyId), { name });
            if (folder && folder.folderId) self.folders.push(toFolderSnapshot(folder));
            return folder;
        }),

        renameFolder: flow(function* renameFolder(companyId, folderId, name) {
            const folder = yield self.patchApi(`${foldersBase(companyId)}/${folderId}`, { name });
            const existing = self.folderById(folderId);
            if (existing && folder) existing.name = folder.name || name;
            return folder;
        }),

        // The server moves the folder's documents to Unfiled before removing it.
        deleteFolder: flow(function* deleteFolder(companyId, folderId) {
            const res = yield self.deleteApi(`${foldersBase(companyId)}/${folderId}`);
            self.applyFolderLocally(self.documents.filter(d => d.folderId === folderId).map(d => d.documentId), null);
            const idx = self.folders.findIndex(f => f.folderId === folderId);
            if (idx >= 0) self.folders.splice(idx, 1);
            return res;
        }),

        // folderId null = Unfiled. Ids the server no longer knows come back in `missing`.
        moveDocuments: flow(function* moveDocuments(companyId, documentIds, folderId) {
            const res = yield self.postApi(`${base(companyId)}/move`, { documentIds, folderId: folderId || null });
            self.applyFolderLocally((res && res.moved) || documentIds, folderId || null);
            return res;
        }),
    }));
